/**
 * Reporting Center (WO-C9 + web reporting build-out).
 *
 * Owner/office surface. On the web app this is the heavy-duty reporting home — built out because
 * exporting and printing are cumbersome on a phone. It provides:
 *   • Detailed P&L: income, labor, cost of work, gross margin (+%), net, expenses-by-category,
 *     and per-job profitability (dense table on wide viewports, stacked rows on phones).
 *   • 1099 contractor totals with W-9 risk flags.
 *   • Materials forecast.
 *   • Exports: client-side CSV (per report) + server-built CSVs (income/expense year-end, CPA
 *     year-end, Gusto labor for a date range) downloaded with auth.
 *   • Print / Save-as-PDF: branded, board-ready documents for any report (web only).
 *
 * Native degrades gracefully: data renders on-screen; CSV/print/server-export buttons (web-only)
 * are hidden and a hint points the user to the web app.
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import {
  getPnlDetailed,
  get1099,
  getForecast,
  type PnlDetailed,
  type JobProfitRow,
  type Worker1099Total,
  type MaterialForecast,
  type ForecastBucket,
} from '../../src/api/reports';
import {
  Screen,
  Text,
  Card,
  Button,
  Field,
  Select,
  BarChart,
  DataTable,
  type SelectOption,
  type Column,
} from '../../src/components/ui';
import { theme } from '../../src/theme';
import { useResponsive } from '../../src/hooks/useResponsive';
import { downloadCsv, csvSupported, type CsvCell } from '../../src/lib/csv';
import { downloadAuthedFile, downloadSupported } from '../../src/lib/download';
import { printReport, printSupported, type PrintSection } from '../../src/lib/print';
import { extractErrorMessage, humanize } from '../../src/lib/format';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(value);
}

function formatPct(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value}%`;
}

function bucketLabel(bucket: string): string {
  if (bucket === 'unscheduled') return 'Unscheduled';
  const d = new Date(bucket);
  if (Number.isNaN(d.getTime())) return bucket;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Two-column labeled row inside a card. */
function StatRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <View style={styles.cardRow}>
      <Text variant={strong ? 'bodyStrong' : 'small'} color={strong ? 'primary' : 'secondary'}>
        {label}
      </Text>
      <Text variant={strong ? 'amount' : 'data'}>{value}</Text>
    </View>
  );
}

/** A small row of export/print actions, only the supported ones rendered. */
function ActionRow({ children }: { children: React.ReactNode }) {
  return <View style={styles.actionRow}>{children}</View>;
}

function ErrorBlock({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <View style={styles.errorBlock}>
      <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
        {message}
      </Text>
      <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={onRetry} accessibilityLabel="Retry" />
    </View>
  );
}

// ---------------------------------------------------------------------------
// P&L (detailed)
// ---------------------------------------------------------------------------

function PnlCard({ year }: { year: number }) {
  const { isWide } = useResponsive();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['reports', 'pnl-detailed', year],
    queryFn: () => getPnlDetailed(year),
    staleTime: 30_000,
  });

  return (
    <View style={styles.section}>
      <Text variant="label" color="secondary">
        Profit &amp; Loss — {year}
      </Text>
      <Card>
        {isLoading ? (
          <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading P&L" />
        ) : isError ? (
          <ErrorBlock message={extractErrorMessage(error, 'Failed to load P&L.')} onRetry={refetch} />
        ) : !data ? (
          <Text variant="body" color="secondary">
            No P&amp;L data.
          </Text>
        ) : (
          <PnlBody data={data} year={year} isWide={isWide} />
        )}
      </Card>
    </View>
  );
}

function PnlBody({ data, year, isWide }: { data: PnlDetailed; year: number; isWide: boolean }) {
  const categories = Object.entries(data.expensesByCategory ?? {});
  const chartData = categories.map(([cat, amount]) => ({
    label: humanize(cat) || cat,
    value: Number(amount) || 0,
    valueLabel: formatMoney(Number(amount)),
  }));
  const jobs = data.jobs ?? [];

  const jobColumns: Column<JobProfitRow>[] = [
    { key: 'job', header: 'Job', flex: 2, render: (j) => <Text variant="bodyStrong" numberOfLines={1}>{j.job_name ?? j.job_id}</Text> },
    { key: 'contracted', header: 'Contracted', flex: 1, align: 'right', render: (j) => <Text variant="data">{formatMoney(j.contracted)}</Text> },
    { key: 'cost', header: 'Cost', flex: 1, align: 'right', render: (j) => <Text variant="data">{formatMoney(j.cost)}</Text> },
    { key: 'margin', header: 'Margin', flex: 1, align: 'right', render: (j) => <Text variant="data">{formatMoney(j.margin)}</Text> },
    { key: 'marginPct', header: 'Margin %', flex: 1, align: 'right', render: (j) => <Text variant="data">{formatPct(j.marginPct)}</Text> },
  ];

  function exportCsv() {
    const rows: CsvCell[][] = [
      ['Profit & Loss (detailed)', String(year)],
      ['Income', data.income ?? ''],
      ['Labor cost', data.laborCost ?? ''],
      ['Cost of work', data.costOfWork ?? ''],
      ['Gross margin', data.grossMargin ?? ''],
      ['Gross margin %', data.grossMarginPct ?? ''],
      ['Expenses', data.expenseTotal ?? ''],
      ['Net', data.net ?? ''],
      [],
      ['Expense category', 'Amount'],
      ...categories.map(([cat, amount]) => [humanize(cat) || cat, Number(amount) || 0] as CsvCell[]),
      [],
      ['Job', 'Contracted', 'Cost', 'Margin', 'Margin %'],
      ...jobs.map((j) => [j.job_name ?? j.job_id, j.contracted, j.cost, j.margin, j.marginPct ?? ''] as CsvCell[]),
    ];
    downloadCsv(`beam-pnl-${year}.csv`, rows);
  }

  function print() {
    const sections: PrintSection[] = [
      {
        heading: 'Summary',
        block: {
          kind: 'keyvalue',
          rows: [
            { label: 'Income', value: formatMoney(data.income) },
            { label: 'Labor cost', value: formatMoney(data.laborCost) },
            { label: 'Cost of work', value: formatMoney(data.costOfWork) },
            { label: 'Gross margin', value: `${formatMoney(data.grossMargin)} (${formatPct(data.grossMarginPct)})` },
            { label: 'Expenses', value: formatMoney(data.expenseTotal) },
            { label: 'Net', value: formatMoney(data.net), strong: true },
          ],
        },
      },
    ];
    if (categories.length > 0) {
      sections.push({
        heading: 'Expenses by category',
        block: { kind: 'table', columns: ['Category', 'Amount'], align: ['left', 'right'], rows: categories.map(([c, a]) => [humanize(c) || c, formatMoney(Number(a))]) },
      });
    }
    if (jobs.length > 0) {
      sections.push({
        heading: 'Per-job profitability',
        block: {
          kind: 'table',
          columns: ['Job', 'Contracted', 'Cost', 'Margin', 'Margin %'],
          align: ['left', 'right', 'right', 'right', 'right'],
          rows: jobs.map((j) => [j.job_name ?? j.job_id, formatMoney(j.contracted), formatMoney(j.cost), formatMoney(j.margin), formatPct(j.marginPct)]),
        },
      });
    }
    printReport({ title: `Profit & Loss — ${year}`, subtitle: 'Detailed P&L with per-job profitability', sections });
  }

  return (
    <>
      <StatRow label="Income" value={formatMoney(data.income)} />
      <StatRow label="Labor cost" value={formatMoney(data.laborCost)} />
      <StatRow label="Cost of work" value={formatMoney(data.costOfWork)} />
      <View style={styles.divider} />
      <StatRow label="Gross margin" value={`${formatMoney(data.grossMargin)} · ${formatPct(data.grossMarginPct)}`} />
      <StatRow label="Operating expenses" value={formatMoney(data.expenseTotal)} />
      <View style={styles.divider} />
      <StatRow label="Net" value={formatMoney(data.net)} strong />

      {categories.length > 0 ? (
        <>
          <Text variant="small" color="secondary" style={styles.subhead}>
            Expenses by category
          </Text>
          <BarChart data={chartData} accessibilityLabel="Expenses by category" />
        </>
      ) : null}

      {jobs.length > 0 ? (
        <>
          <Text variant="small" color="secondary" style={styles.subhead}>
            Per-job profitability
          </Text>
          {isWide ? (
            <DataTable columns={jobColumns} rows={jobs} keyExtractor={(j) => j.job_id} />
          ) : (
            jobs.map((j) => (
              <View key={j.job_id} style={styles.jobRow}>
                <View style={styles.itemMain}>
                  <Text variant="bodyStrong" numberOfLines={1}>{j.job_name ?? j.job_id}</Text>
                  <Text variant="small" color="secondary">
                    {formatMoney(j.contracted)} contracted · {formatMoney(j.cost)} cost · {formatPct(j.marginPct)}
                  </Text>
                </View>
                <Text variant="data">{formatMoney(j.margin)}</Text>
              </View>
            ))
          )}
        </>
      ) : null}

      <ActionRow>
        {csvSupported() ? (
          <Button title="Download CSV" variant="secondary" size="sm" fullWidth={false} onPress={exportCsv} accessibilityLabel="Download P&L as CSV" />
        ) : null}
        {printSupported() ? (
          <Button title="Print / Save PDF" variant="secondary" size="sm" fullWidth={false} onPress={print} accessibilityLabel="Print or save P&L as PDF" />
        ) : null}
      </ActionRow>
    </>
  );
}

// ---------------------------------------------------------------------------
// 1099 contractor totals
// ---------------------------------------------------------------------------

function ContractorCard({ year }: { year: number }) {
  const { isWide } = useResponsive();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['reports', '1099', year],
    queryFn: () => get1099(year),
    staleTime: 30_000,
  });

  const columns: Column<Worker1099Total>[] = [
    { key: 'name', header: 'Contractor', flex: 2, render: (w) => <Text variant="bodyStrong" numberOfLines={1}>{w.full_name}</Text> },
    { key: 'w9', header: 'W-9', flex: 1, render: (w) => (
      <Text variant="small" color={w.needs_w9 ? 'danger' : 'secondary'}>
        {w.w9_on_file ? 'On file' : w.needs_w9 ? 'Needs W-9' : 'No W-9'}
      </Text>
    ) },
    { key: 'paid', header: 'Total paid', flex: 1, align: 'right', render: (w) => <Text variant="data">{formatMoney(w.total_paid)}</Text> },
  ];

  function exportCsv() {
    if (!data) return;
    downloadCsv(`beam-1099-${year}.csv`, [
      ['Contractor', 'W-9 on file', 'Needs W-9', 'Total paid'],
      ...data.map((w) => [w.full_name, w.w9_on_file ? 'yes' : 'no', w.needs_w9 ? 'yes' : 'no', w.total_paid ?? ''] as CsvCell[]),
    ]);
  }

  function print() {
    if (!data) return;
    printReport({
      title: `1099 Contractor Totals — ${year}`,
      subtitle: 'Contractor payments and W-9 status',
      sections: [
        {
          block: {
            kind: 'table',
            columns: ['Contractor', 'W-9', 'Total paid'],
            align: ['left', 'left', 'right'],
            rows: data.map((w) => [w.full_name, w.w9_on_file ? 'On file' : w.needs_w9 ? 'Needs W-9' : 'No W-9', formatMoney(w.total_paid)]),
          },
        },
      ],
    });
  }

  return (
    <View style={styles.section}>
      <Text variant="label" color="secondary">
        1099 Contractor Totals — {year}
      </Text>
      <Card style={styles.listCard}>
        {isLoading ? (
          <View style={styles.cardPad}>
            <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading 1099 totals" />
          </View>
        ) : isError ? (
          <View style={styles.cardPad}>
            <ErrorBlock message={extractErrorMessage(error, 'Failed to load 1099 totals.')} onRetry={refetch} />
          </View>
        ) : !data || data.length === 0 ? (
          <View style={styles.cardPad}>
            <Text variant="body" color="secondary">
              No 1099 contractors for {year}.
            </Text>
          </View>
        ) : (
          <>
            {isWide ? (
              <DataTable columns={columns} rows={data} keyExtractor={(w) => w.worker_id} />
            ) : (
              data.map((w: Worker1099Total) => (
                <View key={w.worker_id} style={styles.itemRow}>
                  <View style={styles.itemMain}>
                    <Text variant="bodyStrong">{w.full_name}</Text>
                    <Text variant="small" color={w.needs_w9 ? 'danger' : 'secondary'}>
                      {w.w9_on_file ? 'W-9 on file' : 'No W-9'}
                      {w.needs_w9 ? ' · Needs W-9' : ''}
                    </Text>
                  </View>
                  <Text variant="data">{formatMoney(w.total_paid)}</Text>
                </View>
              ))
            )}
            <View style={styles.cardPad}>
              <ActionRow>
                {csvSupported() ? (
                  <Button title="Download CSV" variant="secondary" size="sm" fullWidth={false} onPress={exportCsv} accessibilityLabel="Download 1099 totals as CSV" />
                ) : null}
                {printSupported() ? (
                  <Button title="Print / Save PDF" variant="secondary" size="sm" fullWidth={false} onPress={print} accessibilityLabel="Print or save 1099 totals as PDF" />
                ) : null}
              </ActionRow>
            </View>
          </>
        )}
      </Card>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Materials forecast
// ---------------------------------------------------------------------------

function ForecastCard() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['reports', 'forecast', 'materials'],
    queryFn: () => getForecast(),
    staleTime: 30_000,
  });

  return (
    <View style={styles.section}>
      <Text variant="label" color="secondary">
        Materials Forecast
      </Text>
      <Card style={styles.listCard}>
        {isLoading ? (
          <View style={styles.cardPad}>
            <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading materials forecast" />
          </View>
        ) : isError ? (
          <View style={styles.cardPad}>
            <ErrorBlock message={extractErrorMessage(error, 'Failed to load forecast.')} onRetry={refetch} />
          </View>
        ) : !data || data.buckets.length === 0 ? (
          <View style={styles.cardPad}>
            <Text variant="body" color="secondary">
              No upcoming material requirements.
            </Text>
          </View>
        ) : (
          <ForecastBody data={data} />
        )}
      </Card>
    </View>
  );
}

function ForecastBody({ data }: { data: MaterialForecast }) {
  function exportCsv() {
    const rows: CsvCell[][] = [['Bucket', 'Material', 'Planned qty', 'UOM', 'Est. cost']];
    for (const b of data.buckets) {
      for (const m of b.materials) {
        rows.push([bucketLabel(b.bucket), m.material_name, m.planned_quantity, m.uom_code ?? '', m.estimated_cost ?? '']);
      }
    }
    downloadCsv('beam-materials-forecast.csv', rows);
  }

  function print() {
    const rows: (string | number)[][] = [];
    for (const b of data.buckets) {
      for (const m of b.materials) {
        rows.push([bucketLabel(b.bucket), m.material_name, `${m.planned_quantity}${m.uom_code ? ' ' + m.uom_code : ''}`, formatMoney(m.estimated_cost ?? null)]);
      }
    }
    printReport({
      title: 'Materials Forecast',
      subtitle: `Estimated total ${formatMoney(data.estimated_cost_total)}`,
      sections: [{ block: { kind: 'table', columns: ['When', 'Material', 'Planned', 'Est. cost'], align: ['left', 'left', 'left', 'right'], rows } }],
    });
  }

  return (
    <>
      <View style={styles.cardPad}>
        <StatRow label="Estimated total" value={formatMoney(data.estimated_cost_total)} strong />
      </View>
      {data.buckets.map((bucket: ForecastBucket) => (
        <View key={bucket.bucket} style={styles.itemRow}>
          <View style={styles.itemMain}>
            <Text variant="bodyStrong">{bucketLabel(bucket.bucket)}</Text>
            {bucket.materials.map((m) => (
              <Text key={m.material_id} variant="small" color="secondary">
                {m.material_name} · {m.planned_quantity}
                {m.uom_code ? ` ${m.uom_code}` : ''}
                {m.estimated_cost != null ? ` · ${formatMoney(m.estimated_cost)}` : ''}
              </Text>
            ))}
          </View>
          <Text variant="data">{formatMoney(bucket.estimated_cost)}</Text>
        </View>
      ))}
      <View style={styles.cardPad}>
        <ActionRow>
          {csvSupported() ? (
            <Button title="Download CSV" variant="secondary" size="sm" fullWidth={false} onPress={exportCsv} accessibilityLabel="Download materials forecast as CSV" />
          ) : null}
          {printSupported() ? (
            <Button title="Print / Save PDF" variant="secondary" size="sm" fullWidth={false} onPress={print} accessibilityLabel="Print or save materials forecast as PDF" />
          ) : null}
        </ActionRow>
      </View>
    </>
  );
}

// ---------------------------------------------------------------------------
// Server-built exports (auth download)
// ---------------------------------------------------------------------------

function ExportsCard({ year }: { year: number }) {
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(`${year}-12-31`);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(key: string, path: string, fallback: string) {
    setError(null);
    setBusy(key);
    try {
      await downloadAuthedFile(path, fallback);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed.');
    } finally {
      setBusy(null);
    }
  }

  if (!downloadSupported()) {
    return (
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Accounting exports
        </Text>
        <Card>
          <Text variant="bodyStrong">Open Beam in a browser to export</Text>
          <Text variant="small" color="secondary">
            The income/expense year-end, CPA year-end, and Gusto labor exports download from the web app at getabeam.com.
          </Text>
        </Card>
      </View>
    );
  }

  return (
    <View style={styles.section}>
      <Text variant="label" color="secondary">
        Accounting exports
      </Text>
      <Card>
        <Text variant="bodyStrong">Year-end &amp; payroll</Text>
        <Text variant="small" color="secondary">
          Server-built CSVs for your CPA and payroll provider. Downloads are scoped to your account.
        </Text>

        <View style={styles.exportRow}>
          <Button
            title={busy === 'yearend' ? 'Preparing…' : `Income & expense (${year})`}
            variant="secondary"
            size="sm"
            fullWidth={false}
            disabled={busy !== null}
            onPress={() => run('yearend', `/api/reports/year-end/${year}.csv`, `beam-year-end-${year}.csv`)}
            accessibilityLabel="Download income and expense year-end CSV"
          />
          <Button
            title={busy === 'cpa' ? 'Preparing…' : `CPA year-end (${year})`}
            variant="secondary"
            size="sm"
            fullWidth={false}
            disabled={busy !== null}
            onPress={() => run('cpa', `/api/reports/cpa-year-end/${year}.csv`, `beam-cpa-year-end-${year}.csv`)}
            accessibilityLabel="Download CPA year-end CSV"
          />
        </View>

        <View style={styles.divider} />

        <Text variant="bodyStrong">Gusto labor handoff</Text>
        <View style={styles.dateRow}>
          <View style={styles.dateField}>
            <Field label="From" value={from} onChangeText={setFrom} placeholder="YYYY-MM-DD" autoCapitalize="none" autoCorrect={false} accessibilityLabel="Gusto export start date" />
          </View>
          <View style={styles.dateField}>
            <Field label="To" value={to} onChangeText={setTo} placeholder="YYYY-MM-DD" autoCapitalize="none" autoCorrect={false} accessibilityLabel="Gusto export end date" />
          </View>
        </View>
        <Button
          title={busy === 'gusto' ? 'Preparing…' : 'Download Gusto labor CSV'}
          variant="secondary"
          size="sm"
          fullWidth={false}
          disabled={busy !== null}
          onPress={() =>
            run(
              'gusto',
              `/api/reports/gusto-export.csv?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
              `beam-gusto-labor-${from}_${to}.csv`,
            )
          }
          accessibilityLabel="Download Gusto labor CSV for the date range"
        />

        {error ? (
          <Text variant="small" color="danger" accessibilityRole="alert">
            {error}
          </Text>
        ) : null}
      </Card>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export default function ReportsScreen() {
  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo<SelectOption<string>[]>(
    () => [0, 1, 2].map((d) => ({ value: String(currentYear - d), label: String(currentYear - d) })),
    [currentYear],
  );
  const [year, setYear] = useState<string>(String(currentYear));
  const yearNum = parseInt(year, 10);

  return (
    <Screen scroll>
      <Text variant="title">Reporting</Text>
      <Text variant="small" color="secondary">
        {printSupported()
          ? 'Profit & loss, per-job margins, contractors, and forecasting — export to CSV or print board-ready PDFs.'
          : 'Profit & loss, per-job margins, contractors, and forecasting. Open Beam on the web to export and print.'}
      </Text>

      <Select label="Year" options={yearOptions} value={year} onChange={setYear} />

      <PnlCard year={yearNum} />
      <ContractorCard year={yearNum} />
      <ForecastCard />
      <ExportsCard year={yearNum} />
    </Screen>
  );
}

const styles = StyleSheet.create({
  section: { gap: theme.spacing.sm },
  cardRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  divider: { borderTopWidth: theme.layout.hairline, borderTopColor: theme.color.border.default, marginVertical: theme.spacing.xs },
  subhead: { marginTop: theme.spacing.sm },
  listCard: { gap: 0, padding: 0, overflow: 'hidden' },
  cardPad: { paddingHorizontal: theme.spacing.lg, paddingVertical: theme.spacing.md },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    borderTopWidth: theme.layout.hairline,
    borderTopColor: theme.color.border.default,
  },
  jobRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
    paddingVertical: theme.spacing.sm,
    borderTopWidth: theme.layout.hairline,
    borderTopColor: theme.color.border.default,
  },
  itemMain: { flex: 1, gap: theme.spacing.xs },
  actionRow: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.sm, marginTop: theme.spacing.sm },
  exportRow: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.sm, marginTop: theme.spacing.sm },
  dateRow: { flexDirection: 'row', gap: theme.spacing.md, marginTop: theme.spacing.xs },
  dateField: { flex: 1 },
  errorBlock: { gap: theme.spacing.sm },
});
